import { styled, css, Callout, TextInput } from 'canoe';
import { invert } from 'lodash';
import React, { useEffect, useState, useRef, Fragment } from 'react';
import { ColorResult, SketchPicker } from 'react-color';
import tinycolor from 'tinycolor2';

import { ComponentType } from '@candu-ai/styleguide/types';
import { useToggle } from 'commons/hooks';
import { useVariant } from 'dashboard/pages/Styleguide/useVariant';

// we have to repeat the color names here to avoid a circular input...
// refactor in the next pr to DRY things up.
const colorNames = [
  'primary',
  'secondary',
  'tertiary',
  'danger',
  'success',
  'warning',
  'neutral',
  'lines',
];

const Container = styled.div<any>`
  ${({ large }) => css`
    width: ${large ? '100%' : '200px'};
    position: relative;
  `}
`;

const Box = styled.div`
  ${({ theme }) => css`
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: ${theme.spaces.xxs};
  `}
`;

interface PreviewProps {
  disabled: boolean;
  background?: string;
  large: boolean;
}

const Preview = styled.div<PreviewProps>`
  ${({ theme, disabled, large, background }) => css`
    min-width: 40px;
    height: ${large ? '80px' : '40px'};

    border-radius: ${theme.borderRadius.small};
    border: 1px solid ${theme.colors.border};
    cursor: pointer;
    background: ${theme.colors.White};
    position: relative;

    ${background &&
    css`
      background: ${background};
    `}

    ${disabled &&
    css`
      :after {
        content: ' ';
        display: block;
        width: 80px;
        height: 1px;
        position: absolute;
        background: ${theme.colors.Danger};
        transform: rotate(45deg);
        transform-origin: 0 0;
        top: 0;
        left: 0;
      }
    `}
  `}
`;

const PreviewBox = styled.div`
  margin-top: 4px;
`;

interface Props {
  value?: string;
  large?: boolean;
  variables?: Record<string, string>;
  onChange?: (color: string) => void;
  defaultValue?: 'transparent' | 'default';
  isGlobal?: boolean;
  disableAlpha?: boolean;
}

const hexRegexp = new RegExp(/^([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);
const hashHexRegexp = new RegExp(/^#([0-9a-f]{3}|[0-9a-f]{6}|[0-9a-f]{8})$/i);

export const TRANSPARENT = 'transparent';
export const UNSET = 'default';

const isValidColorInput = (inputColor: string) =>
  hashHexRegexp.test(inputColor) || [TRANSPARENT, UNSET].includes(inputColor);

export const ColorPicker = ({
  value,
  onChange,
  defaultValue = TRANSPARENT,
  isGlobal,
  large = false,
  disableAlpha = false,
}: Props) => {
  const { variables } = useVariant(ComponentType.Global, 'base') || {};
  const previewRef = useRef<HTMLDivElement>(null);

  const presetColors = Object.entries(variables ?? {})
    .map(([rule, color]) => ({
      isColor: rule.indexOf('color') > 0,
      title: rule.split('--').reverse()[0].replace('color-', ''),
      color,
    }))
    .filter(({ isColor, title }) => isColor && colorNames.indexOf(title) > -1)
    .sort((a, b) => colorNames.indexOf(a.title) - colorNames.indexOf(b.title));
  const colorVariablesMap = invert(variables || {});

  const [pickerVisible, setPickerVisible] = useState(false);
  const [inputColor, setInputColor] = useState(value);
  const [pickerColor, setPickerColor] = useState(value);

  const [hasFocus, changeFocus] = useToggle();

  const selectColor = (colorValue?: string) => {
    setInputColor(colorValue);
    // picker uses internal state to allow for different input methods
    // but we still forward onChange value to parent and sync value with state
    if (onChange && colorValue) {
      const presetSelected = colorVariablesMap[colorValue];

      onChange(presetSelected && !isGlobal ? `var(${presetSelected})` : colorValue);
    }
  };

  useEffect(() => {
    if (value !== inputColor) {
      setInputColor(value);
      setPickerColor(value);
    }
  }, [value]);

  const colorToHex = (parsedColor: tinycolor.Instance) =>
    !disableAlpha && parsedColor.getAlpha() < 1
      ? parsedColor.toHex8String()
      : parsedColor.toHexString();

  const adaptPickerValue = (pickerValue: ColorResult) => colorToHex(tinycolor(pickerValue.rgb));

  const setColorManually = (newColor: string) => {
    setInputColor(newColor);
    if (hexRegexp.test(newColor)) {
      setColorManually(`#${newColor}`);
    } else if (isValidColorInput(newColor)) {
      setPickerColor(newColor);
      selectColor(newColor);
    }
  };

  const resetColorIfInvalid = () => {
    changeFocus();
    if (inputColor === '') {
      selectColor(defaultValue);
      return;
    }
    if (!inputColor || !isValidColorInput(inputColor)) {
      setInputColor(value);
    }
  };
  let background = value;
  const displayColor = inputColor !== undefined ? inputColor : defaultValue;
  if (displayColor === TRANSPARENT) {
    background =
      'url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAAMUlEQVQ4T2NkYGAQYcAP3uCTZhw1gGGYhAGBZIA/nYDCgBDAm9BGDWAAJyRCgLaBCAAgXwixzAS0pgAAAABJRU5ErkJggg==")';
  }

  if (displayColor === UNSET) {
    background = 'none';
  }

  const preview = (
    <Preview
      large={large}
      ref={previewRef}
      disabled={displayColor === UNSET}
      background={background}
      onClick={() => window.requestAnimationFrame(() => setPickerVisible(!pickerVisible))}
    />
  );

  const RenderBox = large ? Fragment : Box;

  return (
    <Container large>
      <RenderBox>
        <TextInput
          onChange={(e: any) => setColorManually(e.target.value)}
          value={hasFocus && displayColor === TRANSPARENT ? '' : displayColor}
          onBlur={resetColorIfInvalid}
          onFocus={changeFocus}
        />

        {!large && preview}
      </RenderBox>
      {large && <PreviewBox>{preview}</PreviewBox>}

      {pickerVisible && (
        <Callout
          usePortal={false}
          referenceElement={previewRef.current}
          closeKey="Escape"
          placement="bottom-end"
          onClose={() => setPickerVisible(false)}
        >
          {(calloutProps) => (
            <div
              ref={calloutProps.ref}
              style={{ ...calloutProps.style, zIndex: 1 }}
              {...calloutProps.attributes.popper}
            >
              <SketchPicker
                disableAlpha={disableAlpha}
                color={pickerColor}
                onChange={(pickerValue) => setPickerColor(adaptPickerValue(pickerValue))}
                onChangeComplete={(pickerValue) => selectColor(adaptPickerValue(pickerValue))}
                presetColors={presetColors}
              />
            </div>
          )}
        </Callout>
      )}
    </Container>
  );
};
